import {Text} from '@albert-io/atomic';
import {useShallow} from 'zustand/react/shallow';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import React, {useCallback, useEffect} from 'react';
import {WORD_BANK_ID} from '../store';
import WordBank from './WordBank.react';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import DropSlots from './DropSlots';

interface Props {
  question: Record<string, any>;
}

export const CommonBankQuestionArea = ({question}: Props) => {
  const {useStoreHook, initialize, saveDraftGuess} = useQuestionAndDrafts(question);

  const {
    questionInStore,
    wordBankItems,
    placeItemInSlot,
    setActiveItem,
    activeItemId,
    isDragging,
    setIsDragging,
    isSubmitted
  } = useStoreHook(
    useShallow((state) => ({
      questionInStore: state.question,
      wordBankItems: state.wordBank,
      placeItemInSlot: state.placeItemInSlot,
      activeItemId: state.activeItemId,
      setActiveItem: state.setActiveItem,
      isDragging: state.isDragging,
      setIsDragging: state.setIsDragging,
      isSubmitted: state.isSubmitted
    }))
  );

  // Configure sensors for both pointer (drag) and keyboard navigation
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8 // Require 8px movement before drag starts, allows clicks to work
      }
    }),
    useSensor(KeyboardSensor)
  );

  const onDragStart = (_event: DragStartEvent) => {
    setIsDragging(true);
    setActiveItem(null); // Clear click-to-move selection when dragging starts
  };

  const onDrop = (event: DragEndEvent) => {
    const {active, over} = event;
    setIsDragging(false);

    if (!over || active.id === over.id) return;
    placeItemInSlot(active.id as string, over.id as string);
    saveDraftGuess();
  };

  const handleItemClick = useCallback(
    (itemId: string) => {
      if (isSubmitted) return;
      // Don't process clicks during drag operations
      if (isDragging) return;

      // If clicking the already-selected item, deselect it
      if (activeItemId === itemId) {
        setActiveItem(null);
      } else if (activeItemId) {
        // If another item is selected, move the active item to where the clicked item is
        // The clicked item will be sent back to the word bank
        const clickedItem = wordBankItems.find((item) => item.id === itemId);

        if (clickedItem) {
          // Move active item to clicked item's location (which will displace the clicked item)
          placeItemInSlot(activeItemId, clickedItem.placedInSlot || WORD_BANK_ID);
          saveDraftGuess();
        }
        setActiveItem(null);
      } else {
        // Select this item for click-to-move
        setActiveItem(itemId);
      }
    },
    [
      wordBankItems,
      setActiveItem,
      isDragging,
      placeItemInSlot,
      activeItemId,
      saveDraftGuess,
      isSubmitted
    ]
  );

  const handleSlotClick = useCallback(
    (slotId: string) => {
      if (isSubmitted) return;
      // Don't process clicks during drag operations
      if (isDragging) return;

      if (activeItemId) {
        placeItemInSlot(activeItemId, slotId);
        setActiveItem(null);
        saveDraftGuess();
      }
    },
    [activeItemId, placeItemInSlot, setActiveItem, isDragging, saveDraftGuess, isSubmitted]
  );

  const handleBankClick = useCallback(() => {
    handleSlotClick(WORD_BANK_ID);
  }, [handleSlotClick]);

  useEffect(() => {
    if (question && question.size > 0) {
      initialize(question);
    }
  }, [question, initialize]);

  if (!questionInStore) return null;

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDrop}>
      <div className='flex flex-col'>
        <Text size='m' className='mb-x2'>
          Select an item and move it to the correct area.
        </Text>
        <WordBank
          handleItemClick={handleItemClick}
          question={question}
          handleBankClick={handleBankClick}
        />
        <DropSlots
          question={question}
          handleItemClick={handleItemClick}
          handleSlotClick={handleSlotClick}
        />
      </div>
    </DndContext>
  );
};

export default CommonBankQuestionArea;
